import { Form, Input, Collapse } from "antd";
import { InfoCircleFilled } from "@ant-design/icons";
import antTooltipOverlay from "@/shared/styles/antTooltipOverlay.module.css";
import { Loading, LoadingSize } from "@/shared/components/Loading/Loading";
import { TestDisplayName } from "@/shared/components/TestDisplayName/TestDisplayName";
import style from "./style.module.css";
import type { TemplateFormData } from "./types";
import type { TemplateTestDetails } from "./useTemplateTestDetails";

const { TextArea } = Input;
const { Panel } = Collapse;

const INPUT_ID = "composite-formulas";
const DESCRIPTION_ID = "composite-formulas-desc";

const TOOLTIP = `For example:

COMPUTE COMP_Technical = SUM((.50*Z_T29_EL),(.50*Z_T34_LR))
COMPUTE Z_COMP_Technical = (COMP_Technical - 0) / 1`;

interface CompositeFormulasSectionProps {
  testDetails: TemplateTestDetails;
}

export function CompositeFormulasSection({ testDetails }: CompositeFormulasSectionProps) {
  const form = Form.useFormInstance<TemplateFormData>();
  const selectedTests = Form.useWatch("tests", form) ?? [];
  const hasTests = selectedTests.length > 0;
  const isLoadingTestDetails = selectedTests.some((test) => testDetails.loadingIds.has(test.testId));

  return (
    <>
      {hasTests && (
        <div className={style.testScaleKeyContainer}>
          <Collapse expandIconPosition="end" className={style.testScaleKeyCollapse}>
            <Panel
              header={
                <div className={style.testScaleKeyHeader}>
                  <InfoCircleFilled className={style.testScaleKeyIcon} />
                  Test ID & Scale Reference Key
                </div>
              }
              key="testScaleKey"
            >
              {isLoadingTestDetails ? (
                <div className={style.testScaleKeyLoading} role="status">
                  <Loading size={LoadingSize.default} />
                  <p>Loading selected test details...</p>
                </div>
              ) : (
                <div className={style.testScaleKeyContent}>
                  <p className={style.testScaleKeyDescription}>
                    Use the Test IDs and Scale abbreviations below when writing your formulas. For example, to reference
                    two Scales with abbreviations EL and LR with corresponding Test IDs of 29 and 34, the formula would
                    be written as:
                  </p>
                  <pre className={style.testScaleKeyExample}>
                    <code>COMPUTE COMP_Technical = SUM((.50*Z_T29_EL),(.50*Z_T34_LR))</code>
                  </pre>
                  <ul className={style.testScaleKeyList}>
                    {selectedTests.map((selectedTest) => {
                      const details = testDetails.details[selectedTest.testId];
                      // Error is already displayed on the selected test (in the TestSelector list) if details failed to load
                      if (!details) return null;

                      const { test, scales } = details;
                      return (
                        <li key={test.testId} className={style.testScaleKeyItem}>
                          <div className={style.testScaleKeyTestHeader}>
                            <span className={style.testScaleKeyTestName}>
                              <TestDisplayName name={test.name} internalName={test.internalName} />
                            </span>
                            <span className={style.testScaleKeyTestId}>Test ID: {test.testId}</span>
                          </div>
                          {scales.length > 0 ? (
                            <dl className={style.testScaleKeyScales}>
                              {[...scales]
                                .sort((a, b) => a.abbr.localeCompare(b.abbr))
                                .map((scale) => (
                                  <div key={scale.scaleId} className={style.testScaleKeyScale}>
                                    <dt className={style.testScaleKeyScaleAbbr}>
                                      <code>{scale.abbr}</code>
                                    </dt>
                                    <dd className={style.testScaleKeyScaleName}>{scale.name}</dd>
                                  </div>
                                ))}
                            </dl>
                          ) : (
                            <p className={style.testScaleKeyNoScales}>No scales available for this test</p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </Panel>
          </Collapse>
        </div>
      )}

      <Form.Item
        className={style.compositeFormulasField}
        label="Enter Composite Formulas"
        htmlFor={INPUT_ID}
        tooltip={{
          title: TOOLTIP,
          overlayClassName: antTooltipOverlay.tooltipWrapper,
          icon: <InfoCircleFilled />,
        }}
      >
        <p className={style.fieldDescription} id={DESCRIPTION_ID}>
          List the composite formulas in the text space below
        </p>
        <Form.Item
          name="formulas"
          noStyle={true}
          rules={[{ required: true, message: "Please enter composite formulas." }]}
        >
          <TextArea id={INPUT_ID} rows={6} aria-describedby={DESCRIPTION_ID} />
        </Form.Item>
      </Form.Item>
    </>
  );
}
